/**
 * 개요 상단 — 자재 이름 + 수량 간략 요약 (상위 10개).
 *
 * 현장 담당자 테이블 위에 노출. "이름·수량만" 간결하게, 여러 칼럼 그리드로 한눈에
 * 보이게 한다(운영팀 요청). 층별 수량 등 상세·수정은 자재 관리 페이지
 * (/project/:id/materials)에서 — 이 바는 읽기 전용 요약 + 그 페이지 진입 단축키.
 *
 * 데이터는 SiteMaterialService.summary(top=10) 재사용 (MaterialsWidget 과 동일 소스).
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Inventory2 as InventoryIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SiteMaterialService, SiteMaterialSummary } from '../../../services/SiteMaterialService';

interface Props {
  projectId: number;
}

const TOP_N = 10;

const MaterialsSummaryBar: React.FC<Props> = ({ projectId }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SiteMaterialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SiteMaterialService.summary(projectId, TOP_N)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '자재 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // 자재 등록·수정은 현장 설정이 아니라 전용 자재 관리 페이지에 있다
  // (MaterialsWidget 의 "전체 보기" 와 같은 목적지).
  const goToMaterials = () => navigate(`/project/${projectId}/materials`);
  const hasItems = !!summary && summary.item_count > 0;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: hasItems ? 1.25 : 0, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <InventoryIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            자재 수량
          </Typography>
          {summary && (
            <Typography variant="caption" color="text.secondary">
              ({summary.item_count}종 · 총 {summary.total_quantity.toLocaleString('ko-KR')})
            </Typography>
          )}
        </Stack>
        <Tooltip title="자재 관리">
          <IconButton size="small" onClick={goToMaterials} aria-label="자재 관리">
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
          {error}
        </Typography>
      ) : !hasItems ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          등록된 자재가 없습니다.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
          }}
        >
          {summary!.top.map((m) => (
            <Box
              key={m.id}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={m.name}
              >
                {m.name}
              </Typography>
              <Typography variant="body2" color="primary" fontWeight={700} sx={{ flex: 'none' }}>
                {m.quantity.toLocaleString('ko-KR')}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                  {m.unit || '개'}
                </Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MaterialsSummaryBar;
